import { motion } from 'framer-motion'
import { useTheme } from '../hooks/useTheme'
import { useState } from 'react'

// ============================================================================
// COLOR BAR NAVIGATION
// Note: Colors now come from theme.artist palettes and change with theme!
// Each nav item represents one of the 5 artists
// ============================================================================

interface ColorBarNavProps {
  onNavigate: (item: string) => void
}

// Amazon link for physical Modern Art board game with affiliate tag
const PHYSICAL_GAME_URL = 'https://www.amazon.com/CMON-MDA001-Modern-Art/dp/B0716P8GKX?tag=playmodernart-20'

function ColorBarNav({ onNavigate }: ColorBarNavProps) {
  const theme = useTheme()
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)

  // Nav items with artist mapping - colors will come from theme
  const NAV_ITEMS = [
    { label: 'Rules', artistName: 'Solstice Vane' as const },
    { label: 'Gallery', artistName: 'Mira Faux' as const },
    { label: 'Tutorial', artistName: 'Atlas Zero' as const },
    { label: 'Settings', artistName: 'Vesper Kint' as const },
    { label: 'Buy', artistName: 'Luxe Nebula' as const },
  ]

  const handleClick = (item: typeof NAV_ITEMS[0]) => {
    if (item.label === 'Rules') {
      onNavigate('rules')
    } else if (item.label === 'Gallery') {
      onNavigate('gallery')
    } else if (item.label === 'Tutorial') {
      onNavigate('tutorial')
    } else if (item.label === 'Settings') {
      onNavigate('settings')
    } else if (item.label === 'Buy') {
      // Open Amazon link in new tab for physical board game
      window.open(PHYSICAL_GAME_URL, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <nav
      style={{
        position: 'fixed',
        bottom: 32,
        left: 24,
        zIndex: 100,
        display: 'flex',
        gap: '32px',
      }}
    >
      {NAV_ITEMS.map((item) => {
        const isActive = ['Rules', 'Gallery', 'Tutorial', 'Settings', 'Buy'].includes(item.label)
        return (
          <div
            key={item.label}
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              cursor: isActive ? 'pointer' : 'not-allowed',
            }}
            onClick={() => isActive && handleClick(item)}
            onMouseEnter={() => setHoveredItem(item.label)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            {/* Text label - always visible */}
            <span
              style={{
                fontSize: '0.7rem',
                fontWeight: 400,
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                color: isActive ? theme.textMuted : 'rgba(255,255,255,0.15)',
                transition: 'color 0.3s ease',
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </span>
            {/* Colored bar - grows on hover */}
            <div
              data-bar
              style={{
                width: hoveredItem === item.label ? '100%' : '20%',
                height: '2px',
                backgroundColor: theme.artists[item.artistName].primary,
                marginTop: '4px',
                transition: 'width 0.3s ease',
                boxShadow: `0 0 8px ${theme.artists[item.artistName].secondary}80`,
              }}
            />
            {/* Tooltip for Buy button */}
            {item.label === 'Buy' && hoveredItem === 'Buy' && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  marginBottom: '12px',
                  padding: '8px 12px',
                  backgroundColor: 'rgba(10, 10, 10, 0.95)',
                  border: `1px solid ${theme.artists[item.artistName].primary}40`,
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  fontSize: '0.7rem',
                  color: theme.textSecondary,
                  boxShadow: `0 4px 12px rgba(0,0,0,0.4)`,
                  pointerEvents: 'none',
                }}
              >
                Get the physical board game
                {/* Little arrow pointing down */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-4px',
                    left: '12px',
                    width: '8px',
                    height: '8px',
                    backgroundColor: 'rgba(10, 10, 10, 0.95)',
                    borderRight: `1px solid ${theme.artists[item.artistName].primary}40`,
                    borderBottom: `1px solid ${theme.artists[item.artistName].primary}40`,
                    transform: 'rotate(45deg)',
                  }}
                />
              </div>
            )}
          </div>
        )
      })}
    </nav>
  )
}

// ============================================================================
// LANDING PAGE
// ============================================================================

export interface LandingPageProps {
  onPlay: () => void
  onTutorial?: () => void
}

export function LandingPage({ onPlay, onTutorial }: LandingPageProps) {
  const theme = useTheme()

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      {/* Main content */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 24px',
        }}
      >
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          style={{ textAlign: 'center', marginBottom: 60 }}
        >
          <h1
            style={{
              fontSize: 'clamp(3.5rem, 12vw, 9rem)',
              fontWeight: 200,
              letterSpacing: '0.15em',
              color: theme.text,
              margin: 0,
              lineHeight: 1,
              textShadow: '0 4px 30px rgba(0,0,0,0.5)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            }}
          >
            BOUGIE
          </h1>
          <h1
            style={{
              fontSize: 'clamp(3.5rem, 12vw, 9rem)',
              fontWeight: 200,
              letterSpacing: '0.15em',
              margin: 0,
              lineHeight: 1,
              background: theme.buttonPrimary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: 'none',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            }}
          >
            ART
          </h1>
          <p
            style={{
              fontSize: '1rem',
              color: theme.textMuted,
              marginTop: 24,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              fontWeight: 300,
            }}
          >
            The Money Laundering Game
          </p>
        </motion.div>

        {/* Play button - using CSS for snappy interactions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
          <button
            onClick={onPlay}
            style={{
              padding: '20px 56px',
              fontSize: '1.1rem',
              fontWeight: 500,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#0a0a0a',
              background: theme.buttonPrimary,
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              boxShadow: theme.shadow,
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)'
              // Use a slightly stronger shadow on hover
              e.currentTarget.style.boxShadow = theme.primaryHover === '#E5C158'
                ? '0 8px 40px rgba(201,162,39,0.4)'
                : theme.shadow
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
              e.currentTarget.style.boxShadow = theme.shadow
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.98)'
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)'
            }}
          >
            Play Offline
          </button>

          {/* Tutorial button */}
          {onTutorial && (
            <button
              onClick={onTutorial}
              style={{
                padding: '16px 40px',
                fontSize: '0.9rem',
                fontWeight: 500,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: theme.textSecondary,
                background: 'transparent',
                border: `1px solid ${theme.border}`,
                borderRadius: 6,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = theme.primary
                e.currentTarget.style.borderColor = theme.primaryMuted.replace('0.2', '0.5')
                e.currentTarget.style.background = theme.primaryMuted
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = theme.textSecondary
                e.currentTarget.style.borderColor = theme.border
                e.currentTarget.style.background = 'transparent'
              }}
            >
              Tutorial
            </button>
          )}
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          style={{
            position: 'absolute',
            bottom: 32,
            color: theme.textMuted,
            fontSize: '0.75rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
          }}
        >
          70 Unique Artworks
        </motion.div>
      </div>
    </div>
  )
}

export { ColorBarNav }
